import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Save, Plus, Trash2, Send, Edit, X, Clock, Calendar,
  MessageSquare, CheckCircle2, AlertCircle, XCircle, FileText,
  Phone as PhoneIcon, Key, Shield, Mic,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const META_STATUS_UI = {
  draft:    { label: "Draft",     Icon: FileText,      cls: "border-zinc-700 bg-zinc-800/60 text-zinc-300" },
  pending:  { label: "Meta review", Icon: AlertCircle, cls: "border-amber-500/40 bg-amber-500/10 text-amber-100" },
  approved: { label: "Aprobat",   Icon: CheckCircle2,  cls: "border-emerald-500/40 bg-emerald-500/10 text-emerald-100" },
  rejected: { label: "Respins",   Icon: XCircle,       cls: "border-rose-500/40 bg-rose-500/10 text-rose-200" },
};

const LANGS = [
  { value: "ro", label: "Română (ro)" },
  { value: "ro_RO", label: "Română RO (ro_RO)" },
  { value: "en", label: "Engleză (en)" },
  { value: "en_US", label: "Engleză US (en_US)" },
];

const CATEGORIES = ["UTILITY", "MARKETING", "AUTHENTICATION"];

export default function ApeluriRecenziiSettings({ onSettingsChanged }) {
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [olderThanDays, setOlderThanDays] = useState("30");
  const [maxAgeDays, setMaxAgeDays] = useState("90");
  // Feb 2026 — Surse comenzi (platforme + statusuri) + includere firme.
  const [platforms, setPlatforms] = useState([{ integration_key: "emag_ro_fbe", statuses: [4] }]);
  const [includeCompanies, setIncludeCompanies] = useState(false);
  const [r1Hours, setR1Hours] = useState("24");
  const [r2Hours, setR2Hours] = useState("72");
  const [showTestOnly, setShowTestOnly] = useState(true);
  const [mesajTemplate, setMesajTemplate] = useState("");
  const [mesajLang, setMesajLang] = useState("ro");
  const [sendHour, setSendHour] = useState("10");
  const [r1Tpl, setR1Tpl] = useState("");
  const [r1TplLang, setR1TplLang] = useState("ro");
  const [r2Tpl, setR2Tpl] = useState("");
  const [r2TplLang, setR2TplLang] = useState("ro");
  const [templates, setTemplates] = useState([]);
  const [editing, setEditing] = useState(null); // template object or {new:true}

  // Speach-uri (Feb 2026): scripturi text pentru operator, editabile din UI.
  const [speeches, setSpeeches] = useState([]);
  const [editingSpeech, setEditingSpeech] = useState(null); // speech obj or {new:true}
  // Feb 2026 — mapping speach la 3 puncte-cheie din wizard-ul de status.
  const [speechARaspunsId, setSpeechARaspunsId] = useState("");
  const [speechNuId, setSpeechNuId] = useState("");
  const [speechDaId, setSpeechDaId] = useState("");
  const [autoVoucherOnConfirmed, setAutoVoucherOnConfirmed] = useState(false);

  const loadSpeeches = async () => {
    try {
      const r = await axios.get(`${API}/apeluri-recenzii/settings/speeches`);
      setSpeeches(r.data?.speeches || []);
    } catch (e) {
      toast.error("Nu pot încărca speach-urile", { description: e.response?.data?.detail || e.message });
    }
  };

  const deleteSpeech = async (s) => {
    if (!window.confirm(`Ștergi speach-ul „${s.title}"?`)) return;
    try {
      await axios.delete(`${API}/apeluri-recenzii/settings/speeches/${s.speech_id}`);
      setSpeeches((prev) => prev.filter((x) => x.speech_id !== s.speech_id));
      toast.success("Speach șters");
    } catch (e) {
      toast.error("Ștergere eșuată", { description: e.response?.data?.detail || e.message });
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/apeluri-recenzii/settings`);
      const s = r.data?.settings || {};
      setSettings(s);
      setOlderThanDays(String(s.older_than_days ?? 30));
      setMaxAgeDays(String(s.max_age_days ?? 90));
      setR1Hours(String(s.reminder_1_hours ?? 24));
      setR2Hours(String(s.reminder_2_hours ?? 72));
      setShowTestOnly(Boolean(s.show_test_only));
      setMesajTemplate(s.mesaj_recenzie_template_name || "");
      setMesajLang(s.mesaj_recenzie_language || "ro");
      setSendHour(String(s.reminder_send_hour ?? 10));
      setR1Tpl(s.reminder_1_template_name || "");
      setR1TplLang(s.reminder_1_language || "ro");
      setR2Tpl(s.reminder_2_template_name || "");
      setR2TplLang(s.reminder_2_language || "ro");
      setPlatforms(Array.isArray(s.platforms) && s.platforms.length > 0
        ? s.platforms
        : [{ integration_key: "emag_ro_fbe", statuses: [4] }]);
      setIncludeCompanies(Boolean(s.include_companies));
      setSpeechARaspunsId(s.speech_a_raspuns_id || "");
      setSpeechNuId(s.speech_nu_id || "");
      setSpeechDaId(s.speech_da_id || "");
      setAutoVoucherOnConfirmed(Boolean(s.auto_voucher_on_confirmed));
      setTemplates(r.data?.templates || []);
    } catch (e) {
      toast.error("Nu pot încărca setările", { description: e.response?.data?.detail || e.message });
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); loadSpeeches(); }, []);

  const saveSettings = async () => {
    setSaving(true);
    try {
      const r = await axios.patch(`${API}/apeluri-recenzii/settings`, {
        older_than_days: parseInt(olderThanDays, 10),
        max_age_days: parseInt(maxAgeDays, 10),
        reminder_1_hours: parseInt(r1Hours, 10),
        reminder_2_hours: parseInt(r2Hours, 10),
        show_test_only: Boolean(showTestOnly),
        mesaj_recenzie_template_name: mesajTemplate.trim(),
        mesaj_recenzie_language: mesajLang.trim() || "ro",
        reminder_send_hour: parseInt(sendHour, 10),
        reminder_1_template_name: r1Tpl.trim(),
        reminder_1_language: r1TplLang.trim() || "ro",
        reminder_2_template_name: r2Tpl.trim(),
        reminder_2_language: r2TplLang.trim() || "ro",
        platforms: platforms.filter((p) => p.integration_key && (p.statuses || []).length > 0),
        include_companies: Boolean(includeCompanies),
        speech_a_raspuns_id: speechARaspunsId || "",
        speech_nu_id: speechNuId || "",
        speech_da_id: speechDaId || "",
      });
      setSettings(r.data?.settings);
      toast.success("Setări salvate");
      onSettingsChanged?.(r.data?.settings);
    } catch (e) {
      toast.error("Eșuat", { description: e.response?.data?.detail || e.message });
    } finally { setSaving(false); }
  };

  const submitToMeta = async (t) => {
    const isResubmit = t.meta_status === "rejected" || t.meta_status === "draft";
    const msg = isResubmit
      ? `Retrimiți template-ul „${t.name}" către Meta pentru aprobare?`
      : `Trimiți template-ul „${t.name}" către Meta pentru aprobare?\n\nMeta va procesa cererea în câteva minute/ore. Sincronizarea se face automat.`;
    if (!window.confirm(msg)) return;
    try {
      const r = await axios.post(`${API}/apeluri-recenzii/settings/templates/${t.template_id}/submit`);
      setTemplates((prev) => prev.map((x) => x.template_id === t.template_id ? r.data.template : x));
      const status = r.data?.meta_response?.status || "PENDING";
      toast.success(`Trimis la Meta — status: ${status}`, { description: `ID: ${r.data?.meta_response?.id || "—"}` });
      // Sync silent după ~5s (uneori Meta aprobă template-urile UTILITY în
      // câteva secunde). Polling-ul auto (20s) preia restul.
      setTimeout(() => { syncMetaStatus(true); }, 5000);
    } catch (e) {
      toast.error("Meta a respins cererea", { description: e.response?.data?.detail || e.message });
    }
  };

  const [syncing, setSyncing] = useState(false);
  const syncMetaStatus = async (silent = false) => {
    if (!silent) setSyncing(true);
    try {
      const r = await axios.post(`${API}/apeluri-recenzii/settings/templates/sync-meta-status`);
      setTemplates(r.data?.templates || []);
      if (!silent) {
        toast.success(`Sincronizat cu Meta`, {
          description: `${r.data?.updated || 0}/${templates.length} template-uri actualizate • ${r.data?.meta_count || 0} găsite în Meta`,
        });
      }
      return r.data;
    } catch (e) {
      if (!silent) {
        toast.error("Sincronizare eșuată", { description: e.response?.data?.detail || e.message });
      }
      return null;
    } finally {
      if (!silent) setSyncing(false);
    }
  };

  // Feb 2026 — auto-sync silent la mount + polling la 20s cat timp exista
  // cel putin un template in status `pending`.  Meta poate aproba un template
  // in cateva secunde-minute; user-ul nu mai trebuie sa apese manual pe
  // „Sincronizeaza cu Meta".
  const hasPending = templates.some((t) => t.meta_status === "pending");
  useEffect(() => {
    // Prima încărcare: dacă avem template-uri, cere status refresh imediat.
    if (templates.length === 0) return;
    if (!hasPending) return;
    // Sync silent imediat + apoi la 20s până toate ies din PENDING.
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      await syncMetaStatus(true);
    };
    tick();
    const id = setInterval(tick, 20000);
    return () => { cancelled = true; clearInterval(id); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasPending, templates.length]);

  const deleteTemplate = async (t) => {
    if (!window.confirm(`Ștergi template-ul „${t.name}"?`)) return;
    try {
      await axios.delete(`${API}/apeluri-recenzii/settings/templates/${t.template_id}`);
      setTemplates((prev) => prev.filter((x) => x.template_id !== t.template_id));
      toast.success("Șters");
    } catch (e) {
      toast.error("Eșuat", { description: e.response?.data?.detail || e.message });
    }
  };

  const settingsDirty = settings && (
    parseInt(olderThanDays, 10) !== settings.older_than_days ||
    parseInt(maxAgeDays, 10) !== settings.max_age_days ||
    JSON.stringify(platforms) !== JSON.stringify(settings.platforms || [{ integration_key: "emag_ro_fbe", statuses: [4] }]) ||
    Boolean(includeCompanies) !== Boolean(settings.include_companies) ||
    (speechARaspunsId || "") !== (settings.speech_a_raspuns_id || "") ||
    (speechNuId || "") !== (settings.speech_nu_id || "") ||
    (speechDaId || "") !== (settings.speech_da_id || "") ||
    parseInt(r1Hours, 10) !== settings.reminder_1_hours ||
    parseInt(r2Hours, 10) !== settings.reminder_2_hours ||
    Boolean(showTestOnly) !== Boolean(settings.show_test_only) ||
    mesajTemplate.trim() !== (settings.mesaj_recenzie_template_name || "") ||
    mesajLang.trim() !== (settings.mesaj_recenzie_language || "ro") ||
    parseInt(sendHour, 10) !== (settings.reminder_send_hour ?? 10) ||
    r1Tpl.trim() !== (settings.reminder_1_template_name || "") ||
    r1TplLang.trim() !== (settings.reminder_1_language || "ro") ||
    r2Tpl.trim() !== (settings.reminder_2_template_name || "") ||
    r2TplLang.trim() !== (settings.reminder_2_language || "ro")
  );

  return (
    <div className="space-y-6" data-testid="settings-tab">
      {/* General */}
      <div className="rounded-xl border border-zinc-800 bg-zinc-950 p-4">
        <div className="mb-3 flex items-center gap-2">
          <Clock className="h-4 w-4 text-orange-300" />
          <h2 className="text-sm font-semibold text-zinc-100">Configurare generală</h2>
        </div>
        {/* Test-only toggle — Feb 2026: ascunde comenzile reale până când
            WhatsApp Cloud API e complet configurat & testat. */}
        <label
          className="mb-4 flex items-start gap-3 rounded-lg border border-fuchsia-500/30 bg-fuchsia-500/5 p-3 cursor-pointer"
          data-testid="setting-show-test-only"
        >
          <input
            type="checkbox"
            checked={showTestOnly}
            onChange={(e) => setShowTestOnly(e.target.checked)}
            className="mt-0.5 h-4 w-4 cursor-pointer accent-fuchsia-500"
            data-testid="setting-show-test-only-input"
          />
          <span className="flex-1">
            <span className="block text-sm font-medium text-zinc-100">
              Doar comenzi test (ascunde comenzile reale)
            </span>
            <span className="mt-0.5 block text-[11px] leading-snug text-zinc-400">
              Când e activ, lista afișează doar comenzile adăugate manual din butonul „Comandă test".
              Comenzile importate din eMAG nu apar, iar nicio automatizare (WhatsApp / email) nu se
              declanșează pentru clienți reali. Dezactivează după ce ai testat tot flow-ul cu date fictive.
            </span>
          </span>
        </label>
        <div className="grid gap-3 md:grid-cols-3">
          <SettingsInput
            label="Mai vechi de (zile)"
            hint="Limita superioară: doar comenzi cu vârstă ≥ acest prag apar. Ex: 30 → orice comandă din ultimele 30 zile nu apare încă."
            testId="setting-older-than-days"
            icon={<Calendar className="h-3.5 w-3.5 text-zinc-500" />}
            value={olderThanDays}
            onChange={setOlderThanDays}
            suffix="zile"
          />
          <SettingsInput
            label="Fereastră max (zile)"
            hint="Limita inferioară: comenzile mai vechi de N zile ies automat din listă. Ex: 90 → păstrăm doar comenzile din ultimele 90 zile."
            testId="setting-max-age-days"
            icon={<Calendar className="h-3.5 w-3.5 text-zinc-500" />}
            value={maxAgeDays}
            onChange={setMaxAgeDays}
            suffix="zile"
          />
        </div>

        {/* Feb 2026 — Surse comenzi (platforme + statusuri + firme). */}
        <div className="mt-3 rounded-md border border-sky-500/30 bg-sky-500/5 p-3" data-testid="setting-platforms-card">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-xs font-semibold text-sky-100">Surse comenzi</span>
            <button
              type="button"
              onClick={() => setPlatforms((prev) => [...prev, { integration_key: "emag_ro_fbe", statuses: [4] }])}
              data-testid="add-platform-btn"
              className="inline-flex items-center gap-1 rounded-md border border-sky-500/40 bg-sky-500/10 px-2 py-1 text-[11px] font-medium text-sky-100 hover:bg-sky-500/20"
            >
              + Adaugă platformă
            </button>
          </div>
          <p className="mb-2 text-[10px] leading-snug text-zinc-500">
            Alege ce platforme și ce statusuri de comenzi sunt luate în listă. Default: eMAG RO FBE — status <em>Finalizată</em>.
          </p>
          {platforms.length === 0 && (
            <p className="rounded border border-dashed border-zinc-800 py-3 text-center text-[11px] text-zinc-500">
              Nicio sursă activă — apasă „+ Adaugă platformă".
            </p>
          )}
          <div className="space-y-2">
            {platforms.map((p, idx) => (
              <div key={idx} className="flex flex-wrap items-center gap-2 rounded border border-zinc-800 bg-zinc-950/40 p-2" data-testid={`platform-row-${idx}`}>
                <select
                  value={p.integration_key}
                  onChange={(e) => setPlatforms((prev) => prev.map((x, i) => i === idx ? { ...x, integration_key: e.target.value } : x))}
                  data-testid={`platform-select-${idx}`}
                  className="rounded-md border border-zinc-800 bg-zinc-900/70 px-2 py-1 text-xs text-zinc-100 focus:border-sky-500/60 focus:outline-none"
                >
                  <option value="emag_ro_fbe">eMAG RO FBE</option>
                  <option value="emag_ro_non_fbe">eMAG RO Non-FBE</option>
                  <option value="emag_bg_fbe">eMAG BG FBE</option>
                  <option value="emag_bg_non_fbe">eMAG BG Non-FBE</option>
                  <option value="emag_hu_fbe">eMAG HU FBE</option>
                  <option value="emag_hu_non_fbe">eMAG HU Non-FBE</option>
                </select>
                <div className="flex flex-wrap items-center gap-1.5">
                  {[
                    { v: 1, label: "New" },
                    { v: 2, label: "In Progress" },
                    { v: 3, label: "Prepared" },
                    { v: 4, label: "Finalizată" },
                    { v: 5, label: "Returnată" },
                  ].map((st) => {
                    const checked = (p.statuses || []).includes(st.v);
                    return (
                      <label
                        key={st.v}
                        className={`inline-flex cursor-pointer items-center gap-1 rounded-md border px-1.5 py-0.5 text-[11px] ${
                          checked ? "border-sky-500/50 bg-sky-500/15 text-sky-100" : "border-zinc-800 bg-zinc-900/50 text-zinc-400"
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => setPlatforms((prev) => prev.map((x, i) => {
                            if (i !== idx) return x;
                            const sts = new Set(x.statuses || []);
                            if (sts.has(st.v)) sts.delete(st.v); else sts.add(st.v);
                            return { ...x, statuses: Array.from(sts).sort((a, b) => a - b) };
                          }))}
                          data-testid={`platform-${idx}-status-${st.v}`}
                          className="h-3 w-3 cursor-pointer accent-sky-500"
                        />
                        {st.label}
                      </label>
                    );
                  })}
                </div>
                <button
                  type="button"
                  onClick={() => setPlatforms((prev) => prev.filter((_, i) => i !== idx))}
                  data-testid={`platform-remove-${idx}`}
                  className="ml-auto rounded-md border border-rose-500/40 bg-rose-500/10 px-2 py-0.5 text-[10px] text-rose-200 hover:bg-rose-500/20"
                  title="Scoate această platformă"
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
          <label className="mt-3 flex cursor-pointer items-start gap-2" data-testid="setting-include-companies-wrap">
            <input
              type="checkbox"
              checked={includeCompanies}
              onChange={(e) => setIncludeCompanies(e.target.checked)}
              data-testid="setting-include-companies"
              className="mt-0.5 h-4 w-4 cursor-pointer accent-sky-500"
            />
            <span>
              <span className="text-xs font-medium text-zinc-200">Include firme (SRL / SA / PFA)</span>
              <span className="mt-0.5 block text-[10px] leading-snug text-zinc-500">
                Default: dezactivat — clienții-firmă (identificați după CUI sau nume) sunt săriți automat.
                Bifează pentru a-i afișa și pentru ei în lista de apeluri.
              </span>
            </span>
          </label>
          {/* Feb 2026 — auto-save: la fiecare bifă schimbată aplicăm imediat
              settingul; toast confirmă. Ideal pentru toggle-uri critice care
              modifică flow-ul de trimitere voucher fără intervenția user. */}
          <label className="mt-3 flex cursor-pointer items-start gap-2 rounded-md border border-amber-500/25 bg-amber-500/5 px-2 py-2" data-testid="setting-auto-voucher-wrap">
            <input
              type="checkbox"
              checked={autoVoucherOnConfirmed}
              onChange={async (e) => {
                const v = e.target.checked;
                setAutoVoucherOnConfirmed(v);
                try {
                  const r = await axios.patch(`${API}/apeluri-recenzii/settings`, { auto_voucher_on_confirmed: v });
                  setSettings(r.data?.settings || null);
                  toast.success(v ? "Auto-voucher: ACTIV" : "Auto-voucher: OPRIT");
                  onSettingsChanged?.(r.data?.settings);
                } catch (err) {
                  toast.error("Nu s-a putut salva", { description: err.response?.data?.detail || err.message });
                  setAutoVoucherOnConfirmed(!v);
                }
              }}
              data-testid="setting-auto-voucher-on-confirmed"
              className="mt-0.5 h-4 w-4 cursor-pointer accent-amber-500"
            />
            <span>
              <span className="text-xs font-medium text-amber-100">Trimitere voucher automată la „Recenzie Confirmată"</span>
              <span className="mt-0.5 block text-[10px] leading-snug text-amber-100/60">
                Când e ACTIV: la orice trecere pe status <strong>Recenzie Confirmată</strong>
                (upload dovadă sau override manual) se rezervă și se trimite automat
                următorul voucher disponibil (efect identic cu clickul „Trimite voucher" din WhatsApp).
                Voucherul trece în <strong>Oferit</strong>. Idempotent — nu trimite dublu pentru aceeași comandă.
                Se salvează instant la bifare.
              </span>
            </span>
          </label>
        </div>
        {/* Butonul „Da – Trimite Mesaj" din wizard-ul de status folosește
            template-ul de mai jos. Numele trebuie să fie EXACT ca cel
            aprobat în Meta (case-sensitive). */}
        <div className="mt-3 grid gap-3 md:grid-cols-3">
          <label className="block md:col-span-2" data-testid="setting-mesaj-recenzie-template-wrap">
            <span className="mb-1 flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wider text-zinc-400">
              <MessageSquare className="h-3.5 w-3.5 text-emerald-300" />
              Template „Mesaj Recenzie" (Meta)
            </span>
            <input
              type="text"
              value={mesajTemplate}
              onChange={(e) => setMesajTemplate(e.target.value)}
              placeholder="ex: mesaj_recenzie_v1"
              data-testid="setting-mesaj-recenzie-template"
              autoComplete="off"
              spellCheck={false}
              className="w-full rounded-md border border-zinc-800 bg-zinc-900/70 px-2 py-1.5 text-sm text-zinc-100 focus:border-emerald-500/60 focus:outline-none"
            />
            <span className="mt-1 block text-[10px] leading-snug text-zinc-500">
              Numele exact al template-ului aprobat în Meta Business Manager. Folosit când apeși
              „Da – Trimite Mesaj" în wizard-ul de status. Lasă gol pentru a dezactiva trimiterea.
            </span>
          </label>
          <label className="block" data-testid="setting-mesaj-recenzie-lang-wrap">
            <span className="mb-1 flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wider text-zinc-400">
              Limbă
            </span>
            <select
              value={mesajLang}
              onChange={(e) => setMesajLang(e.target.value)}
              data-testid="setting-mesaj-recenzie-lang"
              className="w-full rounded-md border border-zinc-800 bg-zinc-900/70 px-2 py-1.5 text-sm text-zinc-100 focus:border-emerald-500/60 focus:outline-none"
            >
              <option value="ro">Română (ro)</option>
              <option value="ro_RO">Română (ro_RO)</option>
              <option value="en">English (en)</option>
              <option value="en_US">English (en_US)</option>
            </select>
          </label>
        </div>
        {/* R1 + R2 templates + oră trimitere. User cerință Feb 2026:
            scheduler-ul trimite aceste template-uri automat după
            `reminder_N_hours` (zile lucrătoare, ora `send_hour`, skip
            weekend). Bifarea manuală trimite instant fără să aștepte. */}
        <div className="mt-4 space-y-3 rounded-lg border border-orange-500/25 bg-orange-500/5 p-3">
          <div className="flex items-center gap-2">
            <Clock className="h-3.5 w-3.5 text-orange-300" />
            <span className="text-xs font-semibold text-orange-100">Remindere auto (R1 / R2)</span>
          </div>
          <div className="grid gap-3 md:grid-cols-3">
            <label className="block" data-testid="setting-send-hour-wrap">
              <span className="mb-1 block text-[11px] uppercase tracking-wider text-zinc-400">Ora trimitere</span>
              <div className="flex items-center gap-1">
                <input
                  type="number" min="0" max="23"
                  value={sendHour}
                  onChange={(e) => setSendHour(e.target.value)}
                  data-testid="setting-send-hour"
                  className="w-16 rounded-md border border-zinc-800 bg-zinc-900/70 px-2 py-1.5 text-sm text-zinc-100 focus:border-orange-500/60 focus:outline-none"
                />
                <span className="text-xs text-zinc-500">:00 (RO)</span>
              </div>
              <span className="mt-1 block text-[10px] text-zinc-500">Ora zilnică (RO) când scheduler-ul poate trimite.</span>
            </label>
            <label className="block" data-testid="setting-r1-hours-wrap">
              <span className="mb-1 block text-[11px] uppercase tracking-wider text-zinc-400">Întârziere R1</span>
              <div className="flex items-center gap-1">
                <input
                  type="number" min="0" max="8760"
                  value={r1Hours}
                  onChange={(e) => setR1Hours(e.target.value)}
                  data-testid="setting-r1-hours"
                  className="w-20 rounded-md border border-zinc-800 bg-zinc-900/70 px-2 py-1.5 text-sm text-zinc-100 focus:border-orange-500/60 focus:outline-none"
                />
                <span className="text-xs text-zinc-500">ore</span>
              </div>
              <span className="mt-1 block text-[10px] text-zinc-500">Ore de așteptare după status „De acord" înainte de R1 (zile lucrătoare).</span>
            </label>
            <label className="block" data-testid="setting-r2-hours-wrap">
              <span className="mb-1 block text-[11px] uppercase tracking-wider text-zinc-400">Întârziere R2</span>
              <div className="flex items-center gap-1">
                <input
                  type="number" min="0" max="8760"
                  value={r2Hours}
                  onChange={(e) => setR2Hours(e.target.value)}
                  data-testid="setting-r2-hours"
                  className="w-20 rounded-md border border-zinc-800 bg-zinc-900/70 px-2 py-1.5 text-sm text-zinc-100 focus:border-orange-500/60 focus:outline-none"
                />
                <span className="text-xs text-zinc-500">ore de la R1</span>
              </div>
              <span className="mt-1 block text-[10px] text-zinc-500">Ore după trimiterea R1 (zile lucrătoare).</span>
            </label>
            <label className="block md:col-span-2" data-testid="setting-r1-tpl-wrap">
              <span className="mb-1 block text-[11px] uppercase tracking-wider text-zinc-400">Template R1 (Meta)</span>
              <input
                type="text"
                value={r1Tpl}
                onChange={(e) => setR1Tpl(e.target.value)}
                placeholder="ex: reminder_recenzie_1"
                data-testid="setting-r1-tpl"
                autoComplete="off"
                spellCheck={false}
                className="w-full rounded-md border border-zinc-800 bg-zinc-900/70 px-2 py-1.5 text-sm text-zinc-100 focus:border-orange-500/60 focus:outline-none"
              />
              <span className="mt-1 block text-[10px] text-zinc-500">Nume exact template aprobat Meta. Lasă gol pentru a dezactiva R1 auto.</span>
            </label>
            <label className="block" data-testid="setting-r1-lang-wrap">
              <span className="mb-1 block text-[11px] uppercase tracking-wider text-zinc-400">Limbă R1</span>
              <select value={r1TplLang} onChange={(e) => setR1TplLang(e.target.value)} data-testid="setting-r1-lang"
                className="w-full rounded-md border border-zinc-800 bg-zinc-900/70 px-2 py-1.5 text-sm text-zinc-100 focus:border-orange-500/60 focus:outline-none">
                <option value="ro">ro</option><option value="ro_RO">ro_RO</option>
                <option value="en">en</option><option value="en_US">en_US</option>
              </select>
            </label>
            <label className="block md:col-span-2" data-testid="setting-r2-tpl-wrap">
              <span className="mb-1 block text-[11px] uppercase tracking-wider text-zinc-400">Template R2 (Meta)</span>
              <input
                type="text"
                value={r2Tpl}
                onChange={(e) => setR2Tpl(e.target.value)}
                placeholder="ex: reminder_recenzie_2"
                data-testid="setting-r2-tpl"
                autoComplete="off"
                spellCheck={false}
                className="w-full rounded-md border border-zinc-800 bg-zinc-900/70 px-2 py-1.5 text-sm text-zinc-100 focus:border-orange-500/60 focus:outline-none"
              />
              <span className="mt-1 block text-[10px] text-zinc-500">Trimis după R1 respectând aceleași reguli (72h zile lucrătoare, la ora setată).</span>
            </label>
            <label className="block" data-testid="setting-r2-lang-wrap">
              <span className="mb-1 block text-[11px] uppercase tracking-wider text-zinc-400">Limbă R2</span>
              <select value={r2TplLang} onChange={(e) => setR2TplLang(e.target.value)} data-testid="setting-r2-lang"
                className="w-full rounded-md border border-zinc-800 bg-zinc-900/70 px-2 py-1.5 text-sm text-zinc-100 focus:border-orange-500/60 focus:outline-none">
                <option value="ro">ro</option><option value="ro_RO">ro_RO</option>
                <option value="en">en</option><option value="en_US">en_US</option>
              </select>
            </label>
          </div>
          <p className="rounded-md border border-zinc-800 bg-zinc-950 px-3 py-2 text-[10px] leading-snug text-zinc-400">
            Regulă: la statusurile <strong>CADOU DA</strong> și <strong>CADOU DA (fără mesaj)</strong> cu
            Status Recenzie „Fără Recenzie", scheduler-ul trimite R1 după <strong>reminder_1_hours</strong> ore
            (zile lucrătoare, la ora de mai sus). R2 respectă aceleași reguli, cronometrat de la trimiterea R1.
            <strong> CADOU NU nu primește nimic.</strong> Recenzie Confirmată oprește tot.
            Bifarea manuală în tabel trimite instant. Pentru test, setează <strong>reminder_1_hours=1</strong> +
            oră egală cu ora curentă.
          </p>
        </div>
        <div className="mt-3 flex items-center justify-end">
          <button
            type="button"
            onClick={saveSettings}
            disabled={!settingsDirty || saving || loading}
            data-testid="save-settings-btn"
            className="inline-flex items-center gap-1.5 rounded-md border border-orange-500/50 bg-orange-500/90 px-3 py-1.5 text-sm font-medium text-white hover:bg-orange-400 disabled:opacity-40"
          >
            <Save className="h-3.5 w-3.5" />
            {saving ? "Se salvează…" : "Salvează setările"}
          </button>
        </div>
      </div>

      {/* WhatsApp Cloud API — isolated CRM config */}
      <WhatsappApeluriConfigCard />

      {/* Templates */}
      <div className="rounded-xl border border-zinc-800 bg-zinc-950 p-4">
        <div className="mb-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MessageSquare className="h-4 w-4 text-emerald-300" />
            <h2 className="text-sm font-semibold text-zinc-100">Template-uri WhatsApp</h2>
            <span className="rounded-full bg-zinc-800 px-2 py-0.5 text-[10px] text-zinc-400">{templates.length}</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={syncMetaStatus}
              disabled={syncing || templates.length === 0}
              data-testid="sync-meta-templates-btn"
              className="inline-flex items-center gap-1 rounded-md border border-sky-500/40 bg-sky-500/10 px-2 py-1 text-xs text-sky-100 hover:bg-sky-500/20 disabled:opacity-40"
              title="Preia din Meta statusul actual al template-urilor (APPROVED / PENDING / REJECTED)"
            >
              <AlertCircle className={`h-3 w-3 ${syncing ? "animate-spin" : ""}`} />
              {syncing ? "Sincronizez…" : "Sincronizează cu Meta"}
            </button>
            <button
              type="button"
              onClick={() => setEditing({ new: true })}
              data-testid="add-template-btn"
              className="inline-flex items-center gap-1 rounded-md border border-emerald-500/40 bg-emerald-500/10 px-2 py-1 text-xs text-emerald-100 hover:bg-emerald-500/20"
            >
              <Plus className="h-3 w-3" /> Adaugă template
            </button>
          </div>
        </div>
        <p className="mb-2 text-[11px] text-zinc-500">
          Textele urmează să fie trimise către Meta pentru aprobare (WhatsApp Business API).
          Folosește <span className="font-mono text-zinc-300">{"{{name}}"}</span> și <span className="font-mono text-zinc-300">{"{{link}}"}</span> pentru variabile.
        </p>
        {/* Feb 2026 — variabile prietenoase (mapping intern: {{name}}→{{1}}, {{link}}→{{2}}). */}
        <div className="mb-3 rounded-md border border-emerald-500/20 bg-emerald-500/5 p-2" data-testid="template-vars-help">
          <div className="mb-1 text-[10px] font-medium uppercase tracking-wider text-emerald-300/80">
            Variabile disponibile (Apeluri Recenzii)
          </div>
          <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
            <div className="flex items-center gap-2 text-[11px]">
              <span className="rounded bg-emerald-500/15 px-1.5 py-0.5 font-mono text-emerald-200">{"{{name}}"}</span>
              <span className="text-zinc-400">nume + prenume client din comandă</span>
            </div>
            <div className="flex items-center gap-2 text-[11px]">
              <span className="rounded bg-emerald-500/15 px-1.5 py-0.5 font-mono text-emerald-200">{"{{link}}"}</span>
              <span className="text-zinc-400">link direct spre recenzia produsului pe eMAG</span>
            </div>
          </div>
          <p className="mt-1.5 text-[10px] leading-snug text-zinc-500">
            La trimitere, valorile sunt înlocuite automat din comanda clientului.
            Ex link: <span className="font-mono">https://www.emag.ro/product-feedback/&lt;slug&gt;/pd/&lt;PNK&gt;/review/add?ref=review-box_add-review_btn</span>
          </p>
        </div>
        {templates.length === 0 ? (
          <div className="rounded-md border border-dashed border-zinc-800 py-6 text-center text-xs text-zinc-500">
            Niciun template încă. Apasă „Adaugă template" ca să începi.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-zinc-900/70 text-[11px] uppercase text-zinc-500">
                <tr>
                  <th className="px-3 py-2 text-left font-medium">Nume</th>
                  <th className="px-3 py-2 text-left font-medium">Text</th>
                  <th className="px-3 py-2 text-left font-medium">Limbă</th>
                  <th className="px-3 py-2 text-left font-medium">Categorie</th>
                  <th className="px-3 py-2 text-left font-medium">Status Meta</th>
                  <th className="px-3 py-2 text-right font-medium">Acțiuni</th>
                </tr>
              </thead>
              <tbody>
                {templates.map((t) => {
                  const uiStatus = META_STATUS_UI[t.meta_status] || META_STATUS_UI.draft;
                  const StatusIcon = uiStatus.Icon;
                  return (
                    <tr key={t.template_id} className="border-t border-zinc-900 align-top" data-testid={`template-row-${t.template_id}`}>
                      <td className="px-3 py-3 font-mono text-xs text-zinc-100">{t.name}</td>
                      <td className="px-3 py-3">
                        <div className="max-w-md whitespace-pre-wrap text-[11px] leading-snug text-zinc-300">{t.body}</div>
                      </td>
                      <td className="px-3 py-3 text-xs text-zinc-400">{t.language}</td>
                      <td className="px-3 py-3 text-xs text-zinc-400">{t.category}</td>
                      <td className="px-3 py-3">
                        <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium ${uiStatus.cls}`}>
                          <StatusIcon className="h-3 w-3" />
                          {uiStatus.label}
                        </span>
                        {t.rejected_reason ? (
                          <div className="mt-1 rounded border border-rose-500/30 bg-rose-500/5 px-1.5 py-1 text-[10px] leading-snug text-rose-200" data-testid={`template-reject-reason-${t.template_id}`}>
                            <span className="font-semibold">Motiv Meta: </span>{t.rejected_reason}
                          </div>
                        ) : t.meta_note ? (
                          <div className="mt-1 text-[10px] text-zinc-500">{t.meta_note}</div>
                        ) : null}
                        {t.meta_template_id && (
                          <div className="mt-0.5 font-mono text-[9px] text-zinc-600">ID: {t.meta_template_id}</div>
                        )}
                      </td>
                      <td className="px-3 py-3 text-right">
                        <div className="inline-flex items-center gap-1">
                          <button
                            type="button"
                            onClick={() => setEditing(t)}
                            title="Editează"
                            data-testid={`template-edit-${t.template_id}`}
                            className="rounded p-1 text-zinc-400 hover:bg-zinc-800 hover:text-zinc-100"
                          >
                            <Edit className="h-3.5 w-3.5" />
                          </button>
                          {t.meta_status !== "approved" && (
                            <button
                              type="button"
                              onClick={() => submitToMeta(t)}
                              title={t.meta_status === "rejected" ? "Retrimite la Meta după corecție" : "Trimite la Meta (aprobare)"}
                              data-testid={`template-submit-${t.template_id}`}
                              disabled={t.meta_status === "pending"}
                              className={`rounded p-1 disabled:opacity-40 ${t.meta_status === "rejected" ? "text-amber-300 hover:bg-amber-500/10 hover:text-amber-100" : "text-sky-400 hover:bg-sky-500/10 hover:text-sky-200"}`}
                            >
                              <Send className="h-3.5 w-3.5" />
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => deleteTemplate(t)}
                            title="Șterge"
                            data-testid={`template-delete-${t.template_id}`}
                            className="rounded p-1 text-rose-400 hover:bg-rose-500/10 hover:text-rose-200"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editing && (
        <TemplateModal
          template={editing.new ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={(saved) => {
            setTemplates((prev) => {
              const exists = prev.find((x) => x.template_id === saved.template_id);
              return exists
                ? prev.map((x) => x.template_id === saved.template_id ? saved : x)
                : [saved, ...prev];
            });
            setEditing(null);
          }}
        />
      )}

      {/* Speach-uri — scripturi text pentru operator */}
      <div className="rounded-xl border border-zinc-800 bg-zinc-950 p-4" data-testid="speeches-card">
        <div className="mb-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Mic className="h-4 w-4 text-amber-300" />
            <h2 className="text-sm font-semibold text-zinc-100">Speach-uri</h2>
            <span className="rounded-full bg-zinc-800 px-2 py-0.5 text-[10px] text-zinc-400" data-testid="speeches-count">
              {speeches.length}
            </span>
          </div>
          <button
            type="button"
            onClick={() => setEditingSpeech({ new: true })}
            data-testid="add-speech-btn"
            className="inline-flex items-center gap-1 rounded-md border border-amber-500/40 bg-amber-500/10 px-2 py-1 text-xs text-amber-100 hover:bg-amber-500/20"
          >
            <Plus className="h-3 w-3" /> Adaugă speach
          </button>
        </div>
        <p className="mb-3 text-[11px] text-zinc-500">
          Scripturi text pe care le poți folosi manual în timpul apelurilor.
          Titlu scurt + textul complet dedesubt — fără legătură cu Meta / WhatsApp.
        </p>

        {/* Feb 2026 — mapping speach la 3 pași din wizard-ul de status.
            Auto-save la schimbare — user nu trebuie să apese „Salvează setările"
            separat, pt că panelul e departe de butonul de save. */}
        {speeches.length > 0 && (
          <div className="mb-3 rounded-md border border-emerald-500/20 bg-emerald-500/5 p-2" data-testid="speech-mapping">
            <div className="mb-2 text-[10px] font-medium uppercase tracking-wider text-emerald-300/80">
              Afișare speach în wizard-ul de status
            </div>
            <div className="grid gap-2 md:grid-cols-3">
              {[
                { key: "a_raspuns", label: `După „A răspuns”`, value: speechARaspunsId, setter: setSpeechARaspunsId, field: "speech_a_raspuns_id", testid: "speech-map-a-raspuns" },
                { key: "da",        label: `După „Da (acord)”`, value: speechDaId, setter: setSpeechDaId, field: "speech_da_id", testid: "speech-map-da" },
                { key: "nu",        label: `După „Nu (refuză)”`, value: speechNuId, setter: setSpeechNuId, field: "speech_nu_id", testid: "speech-map-nu" },
              ].map((m) => (
                <label key={m.key} className="block">
                  <span className="mb-1 block text-[10px] font-medium text-zinc-400">{m.label}</span>
                  <select
                    value={m.value}
                    onChange={async (e) => {
                      const v = e.target.value;
                      m.setter(v);
                      try {
                        const r = await axios.patch(`${API}/apeluri-recenzii/settings`, { [m.field]: v });
                        setSettings(r.data?.settings || null);
                        toast.success("Mapping speach salvat");
                        onSettingsChanged?.(r.data?.settings);
                      } catch (err) {
                        toast.error("Nu s-a putut salva", { description: err.response?.data?.detail || err.message });
                      }
                    }}
                    data-testid={m.testid}
                    className="w-full rounded-md border border-zinc-800 bg-zinc-900/70 px-2 py-1 text-xs text-zinc-100 focus:border-emerald-500/60 focus:outline-none"
                  >
                    <option value="">— fără speach —</option>
                    {speeches.map((s) => (
                      <option key={s.speech_id} value={s.speech_id}>{s.title}</option>
                    ))}
                  </select>
                </label>
              ))}
            </div>
            <p className="mt-1.5 text-[10px] leading-snug text-zinc-500">
              Se salvează automat la selecție. Butonul „Afișează speach" apare în wizard-ul de status doar dacă ai ales unul aici.
            </p>
          </div>
        )}
        {speeches.length === 0 ? (
          <div className="rounded-md border border-dashed border-zinc-800 py-6 text-center text-xs text-zinc-500" data-testid="speeches-empty">
            Niciun speach încă. Apasă „Adaugă speach" ca să începi.
          </div>
        ) : (
          <div className="space-y-2">
            {speeches.map((s) => (
              <div
                key={s.speech_id}
                className="group rounded-md border border-zinc-800 bg-zinc-900/40 p-3 hover:border-zinc-700"
                data-testid={`speech-row-${s.speech_id}`}
              >
                <div className="mb-1 flex items-start justify-between gap-3">
                  <h3 className="text-sm font-semibold text-zinc-100" data-testid={`speech-title-${s.speech_id}`}>
                    {s.title}
                  </h3>
                  <div className="flex items-center gap-1 opacity-70 group-hover:opacity-100">
                    <button
                      type="button"
                      onClick={() => setEditingSpeech(s)}
                      title="Editează"
                      data-testid={`speech-edit-${s.speech_id}`}
                      className="rounded p-1 text-zinc-400 hover:bg-zinc-800 hover:text-zinc-100"
                    >
                      <Edit className="h-3.5 w-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => deleteSpeech(s)}
                      title="Șterge"
                      data-testid={`speech-delete-${s.speech_id}`}
                      className="rounded p-1 text-zinc-400 hover:bg-rose-500/10 hover:text-rose-300"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
                <pre
                  className="whitespace-pre-wrap font-sans text-[12px] leading-relaxed text-zinc-300"
                  data-testid={`speech-body-${s.speech_id}`}
                >{s.body}</pre>
              </div>
            ))}
          </div>
        )}
      </div>

      {editingSpeech && (
        <SpeechModal
          speech={editingSpeech.new ? null : editingSpeech}
          onClose={() => setEditingSpeech(null)}
          onSaved={(saved) => {
            setSpeeches((prev) => {
              const exists = prev.find((x) => x.speech_id === saved.speech_id);
              return exists
                ? prev.map((x) => x.speech_id === saved.speech_id ? saved : x)
                : [saved, ...prev];
            });
            setEditingSpeech(null);
          }}
        />
      )}
    </div>
  );
}

function SettingsInput({ label, hint, testId, value, onChange, icon, suffix }) {
  return (
    <label className="block rounded-md border border-zinc-800 bg-zinc-900/40 p-2.5">
      <span className="mb-1 flex items-center gap-1.5 text-[11px] font-medium text-zinc-300">
        {icon}{label}
      </span>
      <div className="flex items-center gap-2">
        <input
          type="number"
          min="0"
          max="8760"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testId}
          className="w-24 rounded-md border border-zinc-800 bg-zinc-950 px-2 py-1 text-sm text-zinc-100 focus:border-orange-500/50 focus:outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none"
        />
        {suffix && <span className="text-[11px] text-zinc-500">{suffix}</span>}
      </div>
      <span className="mt-1 block text-[10px] leading-snug text-zinc-500">{hint}</span>
    </label>
  );
}

function TemplateModal({ template, onClose, onSaved }) {
  const [name, setName] = useState(template?.name || "");
  const [body, setBody] = useState(template?.body || "");
  const [language, setLanguage] = useState(template?.language || "ro");
  const [category, setCategory] = useState(template?.category || "UTILITY");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const isNew = !template;

  const submit = async (e) => {
    e?.preventDefault?.();
    setError(null);
    if (!name.trim()) return setError("Numele e obligatoriu.");
    if (!body.trim()) return setError("Textul e obligatoriu.");
    setSaving(true);
    try {
      const payload = { name: name.trim(), body: body.trim(), language, category };
      const r = isNew
        ? await axios.post(`${API}/apeluri-recenzii/settings/templates`, payload)
        : await axios.patch(`${API}/apeluri-recenzii/settings/templates/${template.template_id}`, payload);
      onSaved(r.data.template);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally { setSaving(false); }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onClick={onClose}
      data-testid="template-modal"
    >
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg rounded-xl border border-zinc-800 bg-zinc-950 p-4 shadow-2xl"
      >
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-zinc-100">
            {isNew ? "Template WhatsApp nou" : `Editează: ${template.name}`}
          </h3>
          <button type="button" onClick={onClose} className="rounded p-1 text-zinc-500 hover:bg-zinc-900 hover:text-zinc-200">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="space-y-2">
          <label className="block">
            <span className="mb-0.5 block text-[11px] text-zinc-400">
              Nume template <span className="text-zinc-600">(fără spații, litere mici)</span>
            </span>
            <input
              value={name}
              onChange={(e) => setName(e.target.value.replace(/\s+/g, "_").toLowerCase())}
              placeholder="ex: reminder_1_ro"
              data-testid="template-name"
              className="w-full rounded-md border border-zinc-800 bg-zinc-900/60 px-2 py-1.5 font-mono text-sm text-zinc-100 placeholder:text-zinc-600 focus:border-zinc-700 focus:outline-none"
              autoFocus
            />
          </label>
          <div className="grid gap-2 md:grid-cols-2">
            <label className="block">
              <span className="mb-0.5 block text-[11px] text-zinc-400">Limbă</span>
              <select
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                data-testid="template-language"
                className="w-full rounded-md border border-zinc-800 bg-zinc-900/60 px-2 py-1.5 text-sm text-zinc-100 focus:border-zinc-700 focus:outline-none"
              >
                {LANGS.map((l) => <option key={l.value} value={l.value}>{l.label}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="mb-0.5 block text-[11px] text-zinc-400">Categorie Meta</span>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                data-testid="template-category"
                className="w-full rounded-md border border-zinc-800 bg-zinc-900/60 px-2 py-1.5 text-sm text-zinc-100 focus:border-zinc-700 focus:outline-none"
              >
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </label>
          </div>
          <label className="block">
            <span className="mb-0.5 block text-[11px] text-zinc-400">
              Text mesaj <span className="text-zinc-600">(folosește {"{{name}}"} și {"{{link}}"} pentru variabile)</span>
            </span>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={6}
              placeholder={"Ex: Salut {{name}}, mulțumim că ai cumpărat de la BRAGUS! Poți lăsa o recenzie aici: {{link}}"}
              data-testid="template-body"
              className="w-full rounded-md border border-zinc-800 bg-zinc-900/60 px-2 py-1.5 text-sm text-zinc-100 placeholder:text-zinc-600 focus:border-zinc-700 focus:outline-none"
            />
          </label>
        </div>
        {error && (
          <div className="mt-2 rounded-md border border-rose-500/40 bg-rose-500/10 px-2 py-1.5 text-[11px] text-rose-200">
            {error}
          </div>
        )}
        <div className="mt-3 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-zinc-800 bg-zinc-900/60 px-3 py-1.5 text-xs text-zinc-300 hover:border-zinc-700"
          >
            Anulează
          </button>
          <button
            type="submit"
            disabled={saving}
            data-testid="template-submit-modal"
            className="inline-flex items-center gap-1 rounded-md border border-emerald-500/50 bg-emerald-500/90 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-400 disabled:opacity-50"
          >
            <Save className="h-3 w-3" />
            {saving ? "Se salvează…" : isNew ? "Adaugă" : "Salvează"}
          </button>
        </div>
      </form>
    </div>
  );
}


/**
 * WhatsappApeluriConfigCard — credențiale Meta Cloud API separate JUST pentru
 * pagina Apeluri Recenzii (CRM). Toate câmpurile sunt opționale în save (poți
 * update-a doar tokenul fără să retastezi restul). Trimiterile inițiate din
 * CRM folosesc automat aceste credențiale când `phone_number_id` e setat.
 * Nicio automatizare, comandă sau mesaj obișnuit NU trece prin acest config.
 */
function WhatsappApeluriConfigCard() {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [phoneNumberId, setPhoneNumberId] = useState("");
  const [wabaId, setWabaId] = useState("");
  const [displayPhone, setDisplayPhone] = useState("");
  const [verifyToken, setVerifyToken] = useState("");
  const [appSecret, setAppSecret] = useState("");
  const [accessToken, setAccessToken] = useState("");
  const [enabled, setEnabled] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/apeluri-recenzii/whatsapp-config`);
      const s = r.data || {};
      setState(s);
      setPhoneNumberId(s.phone_number_id || "");
      setWabaId(s.waba_id || "");
      setDisplayPhone(s.display_phone_number || "");
      setVerifyToken(s.verify_token || "");
      setAppSecret("");
      setAccessToken("");
      setEnabled(Boolean(s.enabled));
    } catch (e) {
      toast.error("Nu pot încărca configul WhatsApp CRM", { description: e.response?.data?.detail || e.message });
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        phone_number_id: phoneNumberId.trim(),
        waba_id: wabaId.trim(),
        display_phone_number: displayPhone.trim(),
        verify_token: verifyToken.trim(),
        enabled: Boolean(enabled),
      };
      if (accessToken.trim()) payload.access_token = accessToken.trim();
      if (appSecret.trim()) payload.app_secret = appSecret.trim();
      const r = await axios.put(`${API}/apeluri-recenzii/whatsapp-config`, payload);
      setState(r.data || {});
      setAccessToken("");
      setAppSecret("");
      toast.success("Config WhatsApp CRM salvat");
    } catch (e) {
      toast.error("Eșuat", { description: e.response?.data?.detail || e.message });
    } finally { setSaving(false); }
  };

  return (
    <div className="rounded-xl border border-emerald-500/30 bg-zinc-950 p-4" data-testid="wa-apeluri-config">
      <div className="mb-3 flex items-center gap-2">
        <PhoneIcon className="h-4 w-4 text-emerald-300" />
        <h2 className="text-sm font-semibold text-zinc-100">
          WhatsApp Cloud API — Config CRM (izolat)
        </h2>
        {state?.has_access_token && (
          <span className="rounded-full border border-emerald-500/50 bg-emerald-500/10 px-2 py-0.5 text-[10px] font-medium text-emerald-100">
            Token setat
          </span>
        )}
      </div>
      <p className="mb-3 rounded-lg border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-[11px] leading-snug text-amber-100">
        <strong>Independent 100%</strong> de configul principal (automatizări, orders, messages, retururi).
        Se aplică <strong>doar</strong> pentru trimiteri inițiate din pagina Apeluri Recenzii — inclusiv pe
        comenzile TEST pe care le adaugi manual.
        <br />
        <strong>Fără fallback:</strong> dacă lipsește <code>phone_number_id</code> sau <code>access_token</code>,
        SAU bifa „Activ" e stinsă, sistemul NU trimite nicăieri. Zero risc să iasă un mesaj pe un alt număr.
      </p>

      <div className="grid gap-3 md:grid-cols-2">
        <FieldRow
          label="Phone Number ID"
          hint="ID-ul numerului nou din Meta Business — se găsește la WhatsApp → API Setup."
          testId="wa-crm-phone-id"
          icon={<PhoneIcon className="h-3.5 w-3.5 text-zinc-500" />}
          value={phoneNumberId}
          onChange={setPhoneNumberId}
          placeholder="ex. 105872314529876"
        />
        <FieldRow
          label="WABA ID (Business Account)"
          hint="Aceeași firmă → același WABA (îl copiezi de la configul principal)."
          testId="wa-crm-waba-id"
          icon={<Shield className="h-3.5 w-3.5 text-zinc-500" />}
          value={wabaId}
          onChange={setWabaId}
          placeholder="ex. 118765432198765"
        />
        <FieldRow
          label="Număr afișat (Display Phone)"
          hint="Formatul internațional, ex. +40799123456. Doar informativ, nu afectează API-ul."
          testId="wa-crm-display-phone"
          icon={<PhoneIcon className="h-3.5 w-3.5 text-zinc-500" />}
          value={displayPhone}
          onChange={setDisplayPhone}
          placeholder="+40799123456"
        />
        <FieldRow
          label="Verify Token (webhook)"
          hint="Șir arbitrar (min. 16 caractere), îl pui aici și în Meta la Webhook Verify Token."
          testId="wa-crm-verify-token"
          icon={<Key className="h-3.5 w-3.5 text-zinc-500" />}
          value={verifyToken}
          onChange={setVerifyToken}
          placeholder="ex. crm-verify-2026"
        />
        <FieldRow
          label={state?.has_access_token ? "Access Token (lasă gol dacă nu schimbi)" : "Access Token"}
          hint="System User token permanent din Meta Business Settings → System Users. Începe cu EAAG…"
          testId="wa-crm-access-token"
          icon={<Key className="h-3.5 w-3.5 text-zinc-500" />}
          value={accessToken}
          onChange={setAccessToken}
          type="password"
          placeholder={state?.access_token_preview ? `curent: ${state.access_token_preview}` : "EAAG…"}
        />
        <FieldRow
          label={state?.app_secret_configured ? "App Secret (lasă gol dacă nu schimbi)" : "App Secret (opțional)"}
          hint="Semnătură pentru verificarea payload-urilor webhook. Îl găsești la App Settings → Basic."
          testId="wa-crm-app-secret"
          icon={<Shield className="h-3.5 w-3.5 text-zinc-500" />}
          value={appSecret}
          onChange={setAppSecret}
          type="password"
          placeholder={state?.app_secret_configured ? "••• setat •••" : "opțional"}
        />
      </div>

      <div className="mt-3 flex items-center justify-between gap-3">
        <label
          className="flex items-center gap-2 text-[12px] text-zinc-300"
          data-testid="wa-crm-enabled-wrap"
        >
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
            className="h-4 w-4 cursor-pointer accent-emerald-500"
            data-testid="wa-crm-enabled-input"
          />
          <span>Activ (când e bifat, trimiterile din CRM folosesc acest config)</span>
        </label>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={load}
            disabled={loading || saving}
            data-testid="wa-crm-reload"
            className="inline-flex items-center gap-1 rounded-md border border-zinc-800 bg-zinc-900/60 px-2 py-1 text-xs text-zinc-300 hover:border-zinc-700 disabled:opacity-40"
          >
            Reîncarcă
          </button>
          <button
            type="button"
            onClick={save}
            disabled={loading || saving}
            data-testid="wa-crm-save"
            className="inline-flex items-center gap-1.5 rounded-md border border-emerald-500/50 bg-emerald-500/90 px-3 py-1.5 text-sm font-medium text-white hover:bg-emerald-400 disabled:opacity-50"
          >
            <Save className="h-3.5 w-3.5" />
            {saving ? "Se salvează…" : "Salvează configul"}
          </button>
        </div>
      </div>

      {state?.updated_at && (
        <div className="mt-2 text-[10px] text-zinc-500" data-testid="wa-crm-updated-at">
          Ultima actualizare: {new Date(state.updated_at).toLocaleString("ro-RO")}
        </div>
      )}
    </div>
  );
}

function FieldRow({ label, hint, testId, icon, value, onChange, placeholder, type = "text" }) {
  return (
    <label className="block" data-testid={`${testId}-wrap`}>
      <span className="mb-1 flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wider text-zinc-400">
        {icon}
        {label}
      </span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testId}
        autoComplete="off"
        spellCheck={false}
        className="w-full rounded-md border border-zinc-800 bg-zinc-900/70 px-2 py-1.5 text-sm text-zinc-100 focus:border-emerald-500/60 focus:outline-none"
      />
      {hint && (
        <span className="mt-1 block text-[10px] leading-snug text-zinc-500">{hint}</span>
      )}
    </label>
  );
}


// SpeechModal — creează sau editează un speach (script text)
function SpeechModal({ speech, onClose, onSaved }) {
  const [title, setTitle] = useState(speech?.title || "");
  const [body, setBody] = useState(speech?.body || "");
  const [saving, setSaving] = useState(false);
  const isEdit = Boolean(speech?.speech_id);

  const save = async () => {
    const t = title.trim();
    const b = body.trim();
    if (!t) { toast.error("Titlul e obligatoriu"); return; }
    if (!b) { toast.error("Textul speach-ului e obligatoriu"); return; }
    setSaving(true);
    try {
      let saved;
      if (isEdit) {
        const r = await axios.patch(
          `${API}/apeluri-recenzii/settings/speeches/${speech.speech_id}`,
          { title: t, body: b },
        );
        saved = r.data?.speech;
      } else {
        const r = await axios.post(`${API}/apeluri-recenzii/settings/speeches`, {
          title: t, body: b,
        });
        saved = r.data?.speech;
      }
      toast.success(isEdit ? "Speach actualizat" : "Speach adăugat");
      onSaved?.(saved);
    } catch (e) {
      toast.error("Salvare eșuată", { description: e.response?.data?.detail || e.message });
    } finally { setSaving(false); }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onClick={onClose}
      data-testid="speech-modal"
    >
      <div
        className="w-full max-w-2xl rounded-xl border border-zinc-800 bg-zinc-950 p-4 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-zinc-100">
            {isEdit ? "Editează speach" : "Adaugă speach"}
          </h3>
          <button
            type="button"
            onClick={onClose}
            data-testid="speech-modal-close"
            className="rounded p-1 text-zinc-500 hover:bg-zinc-800 hover:text-zinc-200"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="space-y-3">
          <label className="block">
            <span className="mb-1 block text-[11px] uppercase tracking-wider text-zinc-400">
              Titlu
            </span>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="ex. Introducere apel"
              maxLength={120}
              data-testid="speech-title-input"
              className="w-full rounded-md border border-zinc-800 bg-zinc-900/70 px-3 py-2 text-sm text-zinc-100 focus:border-amber-500/60 focus:outline-none"
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-[11px] uppercase tracking-wider text-zinc-400">
              Text speach
            </span>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={10}
              placeholder="Scrie aici textul complet al speach-ului…"
              data-testid="speech-body-input"
              className="w-full resize-y rounded-md border border-zinc-800 bg-zinc-900/70 px-3 py-2 font-mono text-[12px] leading-relaxed text-zinc-100 focus:border-amber-500/60 focus:outline-none"
            />
            <span className="mt-1 block text-[10px] text-zinc-500">
              Text simplu. Apasă Enter pentru linii noi — se păstrează formatarea la afișare.
            </span>
          </label>
        </div>
        <div className="mt-4 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            data-testid="speech-modal-cancel"
            className="rounded-md border border-zinc-800 bg-zinc-900 px-3 py-1.5 text-xs text-zinc-300 hover:bg-zinc-800"
          >
            Anulează
          </button>
          <button
            type="button"
            onClick={save}
            disabled={saving}
            data-testid="speech-modal-save"
            className="inline-flex items-center gap-1 rounded-md bg-gradient-to-r from-amber-500 to-orange-500 px-3 py-1.5 text-xs font-medium text-white hover:from-amber-400 hover:to-orange-400 disabled:opacity-60"
          >
            <Save className="h-3 w-3" />
            {saving ? "Se salvează…" : (isEdit ? "Salvează" : "Adaugă")}
          </button>
        </div>
      </div>
    </div>
  );
}
